import { currentChainId, switchChain, type Eip1193Provider } from "./eip1193";
import type { WalletSessionController } from "./wallet-session";

export class ContextChangedError extends Error {
  constructor() { super("Wallet, token or network changed. Review the current context and try again."); }
}

/** A pre-broadcast operation belongs to one wallet session and source/deployment scope. */
export class WalletOperation {
  private readonly abort = new AbortController();
  private readonly unsubscribe: () => void;
  private revision: number;
  private readonly identityRevision: number;
  private authenticated: boolean;
  private expectedSwitch: number | null = null;
  readonly signal = this.abort.signal;

  constructor(private readonly wallet: WalletSessionController, private readonly provider: Eip1193Provider,
    private readonly readScope: () => string, private readonly scope: string) {
    this.revision = wallet.getRevision();
    this.identityRevision = wallet.getIdentityRevision();
    this.authenticated = wallet.getSnapshot().authenticated;
    this.unsubscribe = wallet.subscribe(() => {
      const snapshot = wallet.getSnapshot();
      // Only the chain transition explicitly requested by this operation may
      // continue. Account/disconnect/session changes and round trips invalidate it.
      if (this.expectedSwitch !== null && snapshot.chainId === this.expectedSwitch &&
        wallet.getIdentityRevision() === this.identityRevision && wallet.getProvider() === provider &&
        readScope() === scope && !this.signal.aborted) {
        this.revision = wallet.getRevision();
        this.authenticated = snapshot.authenticated;
        this.expectedSwitch = null;
      } else {
        try { this.assertCurrent(); } catch { /* Abort is observed at the await boundary. */ }
      }
    });
  }

  assertCurrent = (): void => {
    if (this.signal.aborted || this.wallet.getRevision() !== this.revision ||
      this.wallet.getIdentityRevision() !== this.identityRevision || this.wallet.getProvider() !== this.provider ||
      this.wallet.getSnapshot().authenticated !== this.authenticated || this.readScope() !== this.scope) {
      this.cancel();
      throw new ContextChangedError();
    }
  };

  async switchTo(chainId: number, resolveRpc?: () => URL | null): Promise<void> {
    this.assertCurrent();
    const current = await currentChainId(this.provider, { signal: this.signal });
    this.assertCurrent();
    if (current !== chainId) {
      this.expectedSwitch = chainId;
      try { await switchChain(this.provider, chainId, this.assertCurrent, resolveRpc); }
      finally { this.expectedSwitch = null; }
      this.assertCurrent();
    }
  }

  cancel(): void { this.abort.abort(new ContextChangedError()); }
  dispose(): void { this.unsubscribe(); }
}
